import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';

export interface Employee {
  id?: string;
  name: string;
  category?: string; // e.g., "Full-time", "Part-time", "Contractor", "Temporary", etc.
  contact?: string; // Contact person name (e.g., manager or HR contact)
  email?: string;
  phone?: string;
  locationIds?: string[]; // Array of location IDs this employee is associated with
  organizationId?: string; // Multi-tenant support
  deleted?: boolean; // Soft delete flag
  createdAt?: any;
  updatedAt?: any;
}

// Employee service
export const employeeService = {
  // Get all employees (excluding deleted ones) for an organization
  getAllEmployees: async (organizationId?: string) => {
    try {
      const employeesRef = collection(db, 'employees');
      
      // Build query with organization filter if provided
      let q;
      if (organizationId) {
        // Filter by organizationId
        q = query(
          employeesRef,
          where('organizationId', '==', organizationId)
        );
      } else {
        q = query(employeesRef, orderBy('name'));
      }
      
      const querySnapshot = await getDocs(q);
      const employees: Employee[] = [];
      querySnapshot.forEach((doc) => {
        const employeeData = { id: doc.id, ...doc.data() } as Employee;
        // Filter out deleted employees
        if (!employeeData.deleted) {
          employees.push(employeeData);
        }
      });
      
      // Sort in memory after fetching to avoid index requirement
      if (organizationId) {
        employees.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }
      
      return { success: true, data: employees };
    } catch (error: any) {
      console.error('Error getting all employees:', error);
      return { success: false, error: error.message };
    }
  },

  // Get a single employee by ID
  getEmployee: async (id: string) => {
    try {
      const employeeRef = doc(db, 'employees', id);
      const docSnap = await getDoc(employeeRef);
      if (docSnap.exists()) {
        const employeeData = { id: docSnap.id, ...docSnap.data() } as Employee;
        // Don't return deleted employees
        if (employeeData.deleted) {
          return { success: false, error: 'Employee not found' };
        }
        return { success: true, data: employeeData };
      } else {
        return { success: false, error: 'Employee not found' };
      }
    } catch (error: any) {
      console.error('Error getting employee:', error);
      return { success: false, error: error.message };
    }
  },

  // Add a new employee
  addEmployee: async (employeeData: Omit<Employee, 'id'>, organizationId?: string) => {
    try {
      const employeesRef = collection(db, 'employees');
      // Trim whitespace from name and other string fields before saving
      const trimmedEmployeeData = {
        ...employeeData,
        name: employeeData.name.trim(),
        ...(employeeData.contact && { contact: employeeData.contact.trim() }),
        ...(employeeData.email && { email: employeeData.email.trim() }),
        ...(employeeData.phone && { phone: employeeData.phone.trim() }),
        ...(employeeData.category && { category: employeeData.category.trim() }),
      };
      const docRef = await addDoc(employeesRef, {
        ...trimmedEmployeeData,
        ...(organizationId && { organizationId }), // Add organizationId if provided
        deleted: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error adding employee:', error);
      return { success: false, error: error.message };
    }
  },

  // Update an existing employee
  updateEmployee: async (id: string, employeeData: Partial<Employee>) => {
    try {
      const employeeRef = doc(db, 'employees', id);
      // Trim whitespace from name and other string fields if being updated
      const trimmedEmployeeData: Partial<Employee> = {
        ...employeeData,
        ...(employeeData.name && { name: employeeData.name.trim() }),
        ...(employeeData.contact && { contact: employeeData.contact.trim() }),
        ...(employeeData.email && { email: employeeData.email.trim() }),
        ...(employeeData.phone && { phone: employeeData.phone.trim() }),
        ...(employeeData.category && { category: employeeData.category.trim() }),
      };
      await updateDoc(employeeRef, {
        ...trimmedEmployeeData,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error: any) {
      console.error('Error updating employee:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete an employee (soft delete)
  deleteEmployee: async (id: string) => {
    try {
      const employeeRef = doc(db, 'employees', id);
      await updateDoc(employeeRef, {
        deleted: true,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting employee:', error);
      return { success: false, error: error.message };
    }
  },
};

